import { openSalesforceEmbeddedTarget } from './salesforceEmbeddedNavigation';

export const HIDDEN_NOTIFICATION_IDS_STORAGE_KEY = 'hidden_notification_ids';
const ACTUAL_UNREAD_NOTIFICATION_COUNT_STORAGE_KEY = 'actual_unread_notification_count';
const LAST_BACKEND_UNREAD_NOTIFICATION_COUNT_STORAGE_KEY = 'last_backend_unread_notification_count';

type AnnouncementAction = {
  label: string;
  url: string;
};

type AnnouncementDetails = Record<string, unknown> & {
  bell_copy?: string | null;
  banner_copy?: string | null;
  external_link?: string | null;
  cta_primary?: AnnouncementAction | null;
};

export type NotificationLike = {
  id: number;
  type: string | null;
  title: string;
  content: string;
  link: string | null;
  cta_text: string | null;
  user_id: string | null;
  account_id: number | null;
  user_read_at: string | null;
  source: string | null;
  details: Record<string, unknown> | null;
  role?: string | null;
  priority: number | null;
  created_at: string;
  updated_at: string;
};

export type NotificationScanSection = {
  title: string;
  items: string[];
};

export type FeatureAnnouncementLike = {
  id: number;
  title: string;
  content: string;
  details?: AnnouncementDetails | null;
  priority?: number | null;
  starts_at?: string | null;
  expires_at?: string | null;
  updated_at?: string | null;
  user_read_at?: string | null;
  user_id?: string | null;
  account_id?: number | null;
  role?: string | null;
};

const EMAIL_INTEGRATION_SCAN_SECTIONS: NotificationScanSection[] = [
  {
    title: 'What it enables',
    items: [
      'Meeting briefings in your inbox for upcoming meetings',
      'AI answers that can use your emails as private context',
      'Email drafts written in your own personal voice',
      'Optional sharing of selected emails and meetings with your company',
    ],
  },
  {
    title: 'Protection & privacy',
    items: [
      'Your emails are kept private and secure',
      'Nothing is shared with anyone else without your explicit permission',
      'Every email and meeting is reviewed for sensitivity, importance, and relevance',
    ],
  },
];

const EMAIL_INTEGRATION_TERMS = [
  'email_integration',
  'email integration',
  'outlook',
  'microsoft 365',
  'mailbox',
  'calendar',
];

const EMAIL_INTEGRATION_TYPES = new Set(['email_integration', 'integration']);

const normalizeIds = (value: unknown): Set<number> => {
  if (!Array.isArray(value)) {
    return new Set();
  }

  return new Set(
    value.filter((item): item is number => typeof item === 'number' && Number.isFinite(item))
  );
};

const normalizeStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
};

const normalizeScanSection = (value: unknown): NotificationScanSection | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const rawSection = value as Record<string, unknown>;
  const title = typeof rawSection.title === 'string' ? rawSection.title.trim() : '';
  const items = normalizeStringList(rawSection.items);
  if (!title || items.length === 0) {
    return null;
  }

  return { title, items };
};

const getStructuredScanSections = (
  details: Record<string, unknown> | null
): NotificationScanSection[] => {
  const rawSections = details?.scan_sections ?? details?.sections;
  if (!Array.isArray(rawSections)) {
    return [];
  }

  return rawSections
    .map(normalizeScanSection)
    .filter((section): section is NotificationScanSection => section !== null);
};

const isEmailIntegrationNotification = (notification: NotificationLike): boolean => {
  const notificationType = notification.type?.toLowerCase() ?? '';
  if (EMAIL_INTEGRATION_TYPES.has(notificationType)) {
    return true;
  }

  const haystack = [
    notification.type,
    notification.title,
    notification.content,
    notification.source,
    String(notification.details?.integration ?? ''),
    String(notification.details?.integration_type ?? ''),
  ]
    .join(' ')
    .toLowerCase();

  return EMAIL_INTEGRATION_TERMS.some(term => haystack.includes(term));
};

export const sortNotificationsNewestFirst = <T extends Pick<NotificationLike, 'created_at'>>(
  notifications: T[]
): T[] =>
  notifications
    .slice()
    .sort(
      (left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
    );

export const getNotificationScanSections = (
  notification: NotificationLike
): NotificationScanSection[] => {
  const structuredSections = getStructuredScanSections(notification.details);
  if (structuredSections.length > 0) {
    return structuredSections;
  }

  if (isEmailIntegrationNotification(notification)) {
    return EMAIL_INTEGRATION_SCAN_SECTIONS;
  }

  return [];
};

export const getNotificationCtaText = (
  notification: Pick<NotificationLike, 'cta_text'>
): string => {
  const ctaText = notification.cta_text?.trim();
  return ctaText || 'Open';
};

export const isExternalNotificationLink = (link: string): boolean => /^https?:\/\//i.test(link);

export const normalizeInternalNotificationLink = (link: string): string | null => {
  const trimmedLink = link.trim();
  if (!trimmedLink || !trimmedLink.startsWith('/') || trimmedLink.startsWith('//')) {
    return null;
  }

  if (trimmedLink === '/profile' || trimmedLink.startsWith('/profile/')) {
    return trimmedLink.replace(/^\/profile(?=\/|$)/, '/user-profile');
  }

  return trimmedLink;
};

export const openNotificationLink = (link: string | null): void => {
  if (!link) {
    return;
  }

  const trimmedLink = link.trim();
  const embeddedResult = openSalesforceEmbeddedTarget(trimmedLink);
  if (embeddedResult.handled) {
    return;
  }

  if (isExternalNotificationLink(trimmedLink)) {
    window.open(trimmedLink, '_blank', 'noopener,noreferrer');
    return;
  }

  const internalLink = normalizeInternalNotificationLink(trimmedLink);
  if (internalLink) {
    window.location.assign(internalLink);
    return;
  }

  console.warn('Blocked unsafe notification link');
};

const normalizeUnreadCount = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return Math.max(0, Math.trunc(value));
};

const readStoredUnreadCount = (storageKey: string): number | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) {
      return null;
    }

    const parsed = Number(raw);
    return normalizeUnreadCount(parsed);
  } catch {
    return null;
  }
};

const writeStoredUnreadCount = (storageKey: string, count: number | null): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (count === null) {
      localStorage.removeItem(storageKey);
      return;
    }

    localStorage.setItem(storageKey, String(Math.max(0, Math.trunc(count))));
  } catch {
    // ignore storage access issues
  }
};

type StoredUnreadCountState = {
  actualUnreadCount: number;
  lastBackendUnreadCount: number | null;
};

const readStoredUnreadCountState = (): StoredUnreadCountState => ({
  actualUnreadCount: readStoredUnreadCount(ACTUAL_UNREAD_NOTIFICATION_COUNT_STORAGE_KEY) ?? 0,
  lastBackendUnreadCount: readStoredUnreadCount(LAST_BACKEND_UNREAD_NOTIFICATION_COUNT_STORAGE_KEY),
});

const writeStoredUnreadCountState = ({
  actualUnreadCount,
  lastBackendUnreadCount,
}: StoredUnreadCountState): void => {
  writeStoredUnreadCount(
    ACTUAL_UNREAD_NOTIFICATION_COUNT_STORAGE_KEY,
    normalizeUnreadCount(actualUnreadCount)
  );
  writeStoredUnreadCount(
    LAST_BACKEND_UNREAD_NOTIFICATION_COUNT_STORAGE_KEY,
    normalizeUnreadCount(lastBackendUnreadCount)
  );
};

export const readHiddenNotificationIds = (): Set<number> => {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const raw = localStorage.getItem(HIDDEN_NOTIFICATION_IDS_STORAGE_KEY);
    if (!raw) {
      return new Set();
    }

    return normalizeIds(JSON.parse(raw));
  } catch {
    return new Set();
  }
};

export const writeHiddenNotificationIds = (ids: Set<number>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(HIDDEN_NOTIFICATION_IDS_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // ignore storage access issues
  }
};

export const hideNotificationLocally = (notificationId: number): Set<number> => {
  const nextIds = readHiddenNotificationIds();
  nextIds.add(notificationId);
  writeHiddenNotificationIds(nextIds);
  return nextIds;
};

export const reconcileActualUnreadCount = (
  backendUnreadCount: number,
  hiddenIds?: Set<number>
): number => {
  const sanitizedBackendUnreadCount = normalizeUnreadCount(backendUnreadCount) ?? 0;
  const storedUnreadCountState = readStoredUnreadCountState();

  const nextActualUnreadCount =
    storedUnreadCountState.lastBackendUnreadCount === null
      ? Math.max(0, sanitizedBackendUnreadCount - (hiddenIds?.size ?? 0))
      : Math.max(
          0,
          storedUnreadCountState.actualUnreadCount +
            (sanitizedBackendUnreadCount - storedUnreadCountState.lastBackendUnreadCount)
        );

  writeStoredUnreadCountState({
    actualUnreadCount: nextActualUnreadCount,
    lastBackendUnreadCount: sanitizedBackendUnreadCount,
  });

  return nextActualUnreadCount;
};

export const adjustActualUnreadCount = ({
  actualDelta,
  backendDelta = 0,
}: {
  actualDelta: number;
  backendDelta?: number;
}): number => {
  const storedUnreadCountState = readStoredUnreadCountState();
  const nextActualUnreadCount = Math.max(
    0,
    storedUnreadCountState.actualUnreadCount + Math.trunc(actualDelta)
  );
  const nextLastBackendUnreadCount =
    storedUnreadCountState.lastBackendUnreadCount === null
      ? null
      : Math.max(0, storedUnreadCountState.lastBackendUnreadCount + Math.trunc(backendDelta));

  writeStoredUnreadCountState({
    actualUnreadCount: nextActualUnreadCount,
    lastBackendUnreadCount: nextLastBackendUnreadCount,
  });

  return nextActualUnreadCount;
};

export const peekLastBackendUnreadCount = (): number | null =>
  readStoredUnreadCountState().lastBackendUnreadCount;

export const setActualUnreadCount = (actualUnreadCount: number): number => {
  const storedUnreadCountState = readStoredUnreadCountState();
  const nextActualUnreadCount = Math.max(0, Math.trunc(actualUnreadCount));

  writeStoredUnreadCountState({
    actualUnreadCount: nextActualUnreadCount,
    lastBackendUnreadCount: storedUnreadCountState.lastBackendUnreadCount,
  });

  return nextActualUnreadCount;
};

export const isAnnouncementType = (type: string | null): boolean =>
  type === 'announcement' || type === 'feature_announcement';

export const isUserScopedNotification = (
  notification: Pick<NotificationLike, 'user_id'>
): boolean => Boolean(notification.user_id);

export const isNotificationRead = (
  notification: Pick<NotificationLike, 'id' | 'user_read_at'>,
  hiddenIds?: Set<number>
): boolean => {
  const effectiveHiddenIds = hiddenIds ?? readHiddenNotificationIds();
  return Boolean(notification.user_read_at) || effectiveHiddenIds.has(notification.id);
};

export const filterVisibleNotifications = <T extends Pick<NotificationLike, 'id' | 'user_read_at'>>(
  notifications: T[],
  hiddenIds?: Set<number>
): T[] => notifications.filter(notification => !isNotificationRead(notification, hiddenIds));

export const normalizeAnnouncementToNotification = (
  announcement: FeatureAnnouncementLike
): NotificationLike => {
  const details = (announcement.details ?? {}) as AnnouncementDetails;
  const ctaPrimary = details.cta_primary ?? null;
  const externalLink = details.external_link ?? null;
  const createdAt =
    announcement.starts_at ??
    announcement.updated_at ??
    announcement.expires_at ??
    new Date().toISOString();
  const updatedAt =
    announcement.updated_at ?? announcement.expires_at ?? announcement.starts_at ?? createdAt;

  return {
    id: announcement.id,
    type: 'announcement',
    title: announcement.title,
    content: details.bell_copy ?? details.banner_copy ?? announcement.content,
    link: ctaPrimary?.url ?? externalLink,
    cta_text: ctaPrimary?.label ?? null,
    user_id: announcement.user_id ?? null,
    account_id: announcement.account_id ?? null,
    user_read_at: announcement.user_read_at ?? null,
    source: 'feature_announcement',
    details: details,
    role: announcement.role ?? null,
    priority: announcement.priority ?? null,
    created_at: createdAt,
    updated_at: updatedAt,
  };
};

export const getTimeGroup = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (date >= todayStart) {
      return 'Today';
    }
    return 'Yesterday';
  }

  if (diffDays === 1) {
    return 'Yesterday';
  }

  if (diffDays < 7) {
    return 'This Week';
  }

  return 'Earlier';
};

export const formatNotificationTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just now';
  }

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }

  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const groupNotifications = <T extends Pick<NotificationLike, 'created_at'>>(
  notifications: T[]
): Map<string, T[]> => {
  const groups = new Map<string, T[]>();
  const order = ['Today', 'Yesterday', 'This Week', 'Earlier'];

  order.forEach(label => {
    groups.set(label, []);
  });

  notifications.forEach(notification => {
    const group = getTimeGroup(notification.created_at);
    const items = groups.get(group);
    if (items) {
      items.push(notification);
      return;
    }
    groups.set(group, [notification]);
  });

  Array.from(groups.entries()).forEach(([label, items]) => {
    if (items.length === 0) {
      groups.delete(label);
    }
  });

  return groups;
};

export const getNotificationTypeLabel = (type: string | null): string => {
  switch (type) {
    case 'alert':
      return 'Alert';
    case 'warning':
      return 'Warning';
    case 'announcement':
    case 'feature_announcement':
      return 'Announcement';
    case 'info':
      return 'Info';
    case 'feature':
    case 'new':
      return 'Feature';
    default:
      return 'Notification';
  }
};
